import {
	AfterViewInit,
	Directive,
	ElementRef,
	inject,
	ViewChild,
} from '@angular/core';
import { AlgoliaPaginatedObject } from '@marmon/frontend/pagination';
import { AlgoliaEntity } from '@marmon/shared/types/algolia';
import { PageSize, SortMod } from '@marmon/shared/types/pagination';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	filter,
	fromEvent,
	map,
	of,
	startWith,
	Subject,
	switchMap,
	takeUntil,
	tap,
	throttleTime,
} from 'rxjs';
import { DestroyService } from '../../services';
import { TableBaseComponent } from '../table-base/table-base.component';

const ScrollThreshold = 100;

@Directive()
export class AlgoliaInfiniteScrollBaseComponent<
	ObjectType extends Pick<AlgoliaEntity, 'objectID'>,
	AdditionalOptions,
>
	extends TableBaseComponent<ObjectType>
	implements AfterViewInit
{
	readonly destroy$ = inject(DestroyService);

	@ViewChild('scroller', { read: ElementRef })
	readonly scrollerRef!: ElementRef<HTMLDivElement>;

	readonly paginatedObject = new AlgoliaPaginatedObject<ObjectType>();

	readonly page$ = new BehaviorSubject<number>(0);
	override readonly size$ = new BehaviorSubject<PageSize>(10);
	readonly additional$ = new BehaviorSubject<AdditionalOptions | null>(null);
	readonly disableInfiniteScroll: boolean = false;

	readonly initView$ = new Subject<void>();

	loadingMore = false;
	readonly scrollStream$ = this.initView$
		.pipe(
			takeUntil(this.destroy$),
			filter(() => {
				const hasScroller = !!this.scrollerRef?.nativeElement;
				if (!hasScroller)
					console.error(
						'The scroller could not be found for infinite scroll',
					);
				return hasScroller && !this.disableInfiniteScroll;
			}),
			switchMap(() =>
				fromEvent(this.scrollerRef.nativeElement, 'scroll'),
			),
			filter(() => {
				const isLoading = this.loadingMore || this.loading$.value;
				return !isLoading && this.paginatedObject.hasMore;
			}),
			map(() => {
				const { clientHeight, scrollTop, scrollHeight } =
					this.scrollerRef.nativeElement;
				const bottom = clientHeight + scrollTop;
				const delta = scrollHeight - bottom;
				return delta < ScrollThreshold;
			}),
			filter((invoke) => !!invoke),
			debounceTime(0),
			tap(() => {
				if (!this.loadingMore) {
					this.loadingMore = true;
					this.page$.next(this.page$.value + 1);
					this.refetch$.next();
				}
			}),
		)
		.subscribe();

	readonly request$ = (
		options: [
			string,
			number,
			PageSize,
			keyof ObjectType | null,
			SortMod,
			AdditionalOptions | null,
			void,
		],
	) => of(options).pipe(map(() => new AlgoliaPaginatedObject<ObjectType>()));

	readonly data$ = combineLatest([
		this.search$.pipe(
			debounceTime(250),
			tap(() => this.page$.next(0)),
		),
		this.page$,
		this.size$,
		this.sorter$,
		this.direction$,
		this.additional$,
		this.refetch$.pipe(throttleTime(500), startWith(undefined)),
	])
		.pipe(
			takeUntil(this.destroy$),
			debounceTime(0),
			tap(() => {
				if (!this.loadingMore) {
					this.loading$.next(true);
				}
			}),
			switchMap((options) => this.request$(options)),
			tap(() => {
				this.loading$.next(false);
				this.loadingMore = false;
			}),
			startWith(this.paginatedObject),
		)
		.subscribe();

	ngAfterViewInit() {
		this.initView$.next();
	}
}
